import { useId, useState } from "react";
import {
  DateTimeField,
  ErrorCodes,
  ProblemError,
  hasGrant,
  usePrincipal,
} from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  Table,
  TableCard,
} from "../../ui";
import { useCarriers } from "../catalog/use-catalog";
import { useChangeRate, useCommissionRates, useCreateRate, useLines } from "./use-commission";
import { ISO_OFFSET_PATTERN, percentToRatio, ratioToPercentDisplay } from "./rate-format";
import type {
  CommissionRateRow,
  CreateCommissionRateChangeInput,
  CreateCommissionRateInput,
} from "../../api/commission-contracts";

// AL2b/AL2d — the Finance commission-rate admin. A seed-view-change flow: view current
// + historical rates (filterable), seed a carrier·line(·product) rate (AL2b), and
// CHANGE an open rate — close-then-insert, effective-dated (AL2d). Append-only and
// reconstruction-preserved: a change closes the prior rate and opens a new dated one,
// never a silent overwrite. No business logic in the browser: the wire value is always
// the (0,1] ratio string (rate-format.ts — reused, never reimplemented), the percentage
// is human display only. RBAC is UX (write gated on commission:write); the API is the
// boundary.
export function CommissionRates() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canWrite = hasGrant(me, "commission:write", "all");

  const carriers = useCarriers();
  const lines = useLines();
  const [filterCarrierId, setFilterCarrierId] = useState("");
  const [filterLineCode, setFilterLineCode] = useState("");
  const rates = useCommissionRates({
    carrierId: filterCarrierId || undefined,
    lineCode: filterLineCode || undefined,
  });

  // Reference lookups so the list shows names, not raw uuids/codes.
  const carrierName = (id: string) => {
    const c = carriers.data?.data.find((x) => x.id === id);
    return c ? `${c.current.tradeName} (${c.code})` : id;
  };
  const lineName = (code: string) => lines.data?.data.find((l) => l.code === code)?.name ?? code;

  const carrierOptions = carriers.data?.data ?? [];
  const lineOptions = lines.data?.data ?? [];

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("comm.heading")}</h1>
          <p className="surf-sub muted">{t("comm.subheading")}</p>
        </div>
      </div>

      <TableCard
        title={t("comm.list.title")}
        data-testid="rate-list"
        actions={
          <div className="surf-filters">
            <Select
              fieldClassName="rate-filter"
              label={t("comm.filter.carrier")}
              value={filterCarrierId}
              onChange={(e) => setFilterCarrierId(e.target.value)}
            >
              <option value="">{t("comm.filter.all")}</option>
              {carrierOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.current.tradeName}
                </option>
              ))}
            </Select>
            <Select
              fieldClassName="rate-filter"
              label={t("comm.filter.line")}
              value={filterLineCode}
              onChange={(e) => setFilterLineCode(e.target.value)}
            >
              <option value="">{t("comm.filter.all")}</option>
              {lineOptions.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </Select>
          </div>
        }
        footer={<span className="muted surf-foot-note">{t("comm.form.appendOnlyNote")}</span>}
      >
        {rates.isLoading && <LoadingState label={t("comm.loading")} rows={3} />}
        {rates.error instanceof ProblemError && <ErrorState title={t("comm.error")} />}
        {rates.data && rates.data.data.length === 0 && (
          <EmptyState data-testid="rate-coldstart" icon="records" title={t("comm.coldStart")} />
        )}

        {rates.data && rates.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("comm.list.carrier")}</th>
                <th>{t("comm.list.line")}</th>
                <th>{t("comm.list.product")}</th>
                <th className="k-th--num">{t("comm.list.rate")}</th>
                <th>{t("comm.list.from")}</th>
                <th>{t("comm.list.to")}</th>
                <th className="rate-td-right">{t("comm.list.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rates.data.data.map((r) => (
                <RateRow
                  key={r.id}
                  rate={r}
                  carrierLabel={carrierName(r.carrierId)}
                  lineLabel={lineName(r.lineCode)}
                  canWrite={canWrite}
                />
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      <SeedRateForm
        canWrite={canWrite}
        carrierOptions={carrierOptions.map((c) => ({
          id: c.id,
          label: `${c.code} — ${c.current.tradeName}`,
        }))}
        lineOptions={lineOptions.map((l) => ({ code: l.code, label: l.name }))}
      />
    </section>
  );
}

// A rate row. The OPEN/current rate (effectiveTo === null) carries the AL2d "change"
// affordance (commission:write); a historical row does not. The inline change form
// closes the prior rate and opens a new dated one (append-only, reconstruction-preserved).
function RateRow({
  rate,
  carrierLabel,
  lineLabel,
  canWrite,
}: {
  rate: CommissionRateRow;
  carrierLabel: string;
  lineLabel: string;
  canWrite: boolean;
}) {
  const { t } = useT();
  const [changing, setChanging] = useState(false);
  const isOpen = rate.effectiveTo === null;

  return (
    <>
      <tr data-testid="rate-row" data-open={isOpen}>
        <td className="cat-strong">{carrierLabel}</td>
        <td>{lineLabel}</td>
        <td>
          {rate.productId ? (
            <code>{rate.productId}</code>
          ) : (
            <span className="muted">{t("comm.allProducts")}</span>
          )}
        </td>
        <td className="k-td--num rate-num">{ratioToPercentDisplay(rate.rate)}</td>
        <td>{rate.effectiveFrom}</td>
        <td>
          {isOpen ? (
            <Badge variant="success">{t("comm.current")}</Badge>
          ) : (
            <span className="muted">
              {rate.effectiveTo} · {t("comm.history")}
            </span>
          )}
        </td>
        <td className="rate-td-right">
          {isOpen && canWrite && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              data-testid="change-rate-btn"
              leadingIcon={<Icon name="edit" size={14} strokeWidth={2} />}
              onClick={() => setChanging((v) => !v)}
            >
              {t("comm.change.action")}
            </Button>
          )}
        </td>
      </tr>
      {isOpen && changing && (
        <tr className="rate-change-row">
          <td colSpan={7}>
            <ChangeRateForm rate={rate} onDone={() => setChanging(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

// AL2d — change an open rate: POST /v1/commission/rates/change (carrierId · lineCode ·
// productId? · rate · effectiveFrom? — NO effectiveTo; the server closes the prior rate
// at the new effectiveFrom). The money rules are REUSED from rate-format.ts (percent in,
// (0,1] ratio string on the wire, reject-don't-round) — never reimplemented.
function ChangeRateForm({ rate, onDone }: { rate: CommissionRateRow; onDone: () => void }) {
  const { t } = useT();
  const change = useChangeRate();
  const [ratePercent, setRatePercent] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");

  const ratio = percentToRatio(ratePercent); // null when malformed / out of (0,1]
  const rateInvalid = ratePercent.trim() !== "" && ratio === null;
  const fromValid = effectiveFrom.trim() === "" || ISO_OFFSET_PATTERN.test(effectiveFrom.trim());
  const fromInvalid = effectiveFrom.trim() !== "" && !fromValid;
  const canSubmit = ratio !== null && fromValid && !change.isPending;

  function submit() {
    // The change contract has NO effectiveTo — the server closes the prior rate.
    const body: CreateCommissionRateChangeInput = {
      carrierId: rate.carrierId,
      lineCode: rate.lineCode,
      rate: ratio!, // the (0,1] ratio string — reused converter, never the percentage
    };
    if (rate.productId) body.productId = rate.productId;
    if (effectiveFrom.trim()) body.effectiveFrom = effectiveFrom.trim();
    change.mutate(body);
  }

  return (
    <div className="rate-changepanel">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("comm.change.heading")}</span>
        <span className="cat-append-pill">
          <Icon name="lock" size={12} strokeWidth={1.9} />
          {t("comm.appendOnlyTag")}
        </span>
      </div>
      <p className="muted surf-note">{t("comm.change.note")}</p>
      <form
        data-testid="change-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <div className="k-form-grid">
          <RatePercentField
            label={t("comm.change.newRate")}
            value={ratePercent}
            onChange={setRatePercent}
            invalid={rateInvalid}
            invalidMessage={t("comm.form.invalidRate")}
            ratio={ratio}
            errorTestId="change-err-rate"
            previewTestId="change-rate-preview"
          />
          <DateTimeField
            label={t("comm.change.effectiveFrom")}
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            error={fromInvalid ? t("comm.form.invalidFrom") : undefined}
            hint={!fromInvalid ? t("comm.change.effectiveFromHint") : undefined}
            data-testid="change-from"
          />
        </div>
        <div className="k-form-actions">
          <Button
            variant="primary"
            size="sm"
            type="submit"
            data-testid="change-submit"
            disabled={!canSubmit}
            loading={change.isPending}
          >
            {t("comm.change.submit")}
          </Button>
          <Button variant="secondary" size="sm" type="button" onClick={onDone}>
            {t("comm.change.cancel")}
          </Button>
          {change.isSuccess && (
            <span role="status" className="form-ok">
              {t("comm.change.success")}
            </span>
          )}
          {change.error instanceof ProblemError && (
            <span role="alert" className="form-error">
              {change.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** The rate (%) money field — a calm, kit-styled input with a live ratio preview and the
 *  reject-don't-round error line. Never alarm-colored on the value itself; the error is a
 *  quiet inline hint. Shared by the seed + change forms so the money tone stays identical. */
function RatePercentField({
  label,
  value,
  onChange,
  invalid,
  invalidMessage,
  ratio,
  errorTestId,
  previewTestId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  invalidMessage: string;
  ratio: string | null;
  errorTestId: string;
  previewTestId: string;
}) {
  const id = useId();
  return (
    <div className="k-field">
      <label className="k-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`k-input${invalid ? " k-input--error" : ""}`}
        inputMode="decimal"
        placeholder="15"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {invalid && (
        <span className="k-field__error" data-testid={errorTestId}>
          <Icon name="alert-circle" size={13} strokeWidth={2.2} />
          {invalidMessage}
        </span>
      )}
      {ratio !== null && (
        <span className="k-field__hint rate-preview" data-testid={previewTestId}>
          = {ratioToPercentDisplay(ratio)} ({ratio})
        </span>
      )}
    </div>
  );
}

function SeedRateForm({
  canWrite,
  carrierOptions,
  lineOptions,
}: {
  canWrite: boolean;
  carrierOptions: Array<{ id: string; label: string }>;
  lineOptions: Array<{ code: string; label: string }>;
}) {
  const { t } = useT();
  const create = useCreateRate();
  const [carrierId, setCarrierId] = useState("");
  const [lineCode, setLineCode] = useState("");
  const [productId, setProductId] = useState("");
  const [ratePercent, setRatePercent] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  const ratio = percentToRatio(ratePercent); // null when malformed / out of (0,1]
  const rateInvalid = ratePercent.trim() !== "" && ratio === null;
  const fromValid = ISO_OFFSET_PATTERN.test(effectiveFrom.trim());
  const fromInvalid = effectiveFrom.trim() !== "" && !fromValid;
  const toValid =
    effectiveTo.trim() === "" ||
    (ISO_OFFSET_PATTERN.test(effectiveTo.trim()) &&
      new Date(effectiveTo) > new Date(effectiveFrom));
  const toInvalid = effectiveTo.trim() !== "" && !toValid;

  const canSubmit =
    canWrite &&
    carrierId !== "" &&
    lineCode !== "" &&
    ratio !== null &&
    fromValid &&
    toValid &&
    !create.isPending;

  function submit() {
    const body: CreateCommissionRateInput = {
      carrierId,
      lineCode,
      rate: ratio!, // the (0,1] ratio string — never the percentage, never a number
      effectiveFrom: effectiveFrom.trim(),
    };
    if (productId.trim()) body.productId = productId.trim();
    if (effectiveTo.trim()) body.effectiveTo = effectiveTo.trim();
    create.mutate(body);
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("comm.form.heading")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("comm.form.readOnly")}</p>}
      <p className="muted surf-note">{t("comm.form.appendOnlyNote")}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Select
            label={t("comm.form.carrier")}
            value={carrierId}
            onChange={(e) => setCarrierId(e.target.value)}
          >
            <option value="">—</option>
            {carrierOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>

          <Select
            label={t("comm.form.line")}
            value={lineCode}
            onChange={(e) => setLineCode(e.target.value)}
          >
            <option value="">—</option>
            {lineOptions.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </Select>

          <Input
            label={t("comm.form.product")}
            placeholder={t("comm.form.productHint")}
            hint={t("comm.form.productHint")}
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          />

          <RatePercentField
            label={t("comm.form.rate")}
            value={ratePercent}
            onChange={setRatePercent}
            invalid={rateInvalid}
            invalidMessage={t("comm.form.invalidRate")}
            ratio={ratio}
            errorTestId="err-rate"
            previewTestId="rate-preview"
          />

          <DateTimeField
            label={t("comm.form.effectiveFrom")}
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            error={fromInvalid ? t("comm.form.invalidFrom") : undefined}
            /* Empty effectiveFrom disables Save (a seed rate REQUIRES a start),
               but the field showed no error and no required marker — so the
               operator could not see what was blocking the save. State it. */
            hint={
              !fromInvalid && effectiveFrom.trim() === "" ? t("comm.form.fromRequired") : undefined
            }
            data-testid="seed-from"
          />

          <DateTimeField
            label={t("comm.form.effectiveTo")}
            value={effectiveTo}
            onChange={setEffectiveTo}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            error={toInvalid ? t("comm.form.invalidTo") : undefined}
            hint={!toInvalid ? t("comm.form.effectiveToHint") : undefined}
            data-testid="seed-to"
          />
        </fieldset>

        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("comm.form.save")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("comm.form.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="conflict-msg">
              {t("comm.form.conflict")}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}
